import BigNumber from "bignumber.js";
import Web3 from "web3";
import {
  formatDisplayNumber,
  formatDisplayUsd,
} from "./formatDisplay";
import Environment from "./Environment";
import ChinaAbi from "./chinaAbi.json";

/** On-chain USD fields (limits, per-wallet counters) use 6 decimals. */
export const PRESALE_USD_DECIMALS = 6;
/** Sale token amounts (gemsRoundCap, totalGemsRoundSold, maxCap) use 18 decimals. */
export const PRESALE_TOKEN_DECIMALS = 18;

/**
 * UI fallbacks while chain limits load. Prefer values from
 * `fetchPresaleRoundLimitsFromChain` / `usePresaleRoundLimits`.
 * On-chain: gemsRoundLimitPerWallet, specialRoundLimit, lowerLimit, upperLimit (6 decimals).
 */
export const GEMS_ROUND_USD_CAP = 2000;
export const SPECIAL_ROUND_USD_CAP = 5000;
export const REGULAR_INVESTED_THRESHOLD_USD = 10000;
export const REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD = 50000;
/** UI fallback bulk discount (20%) while DISCOUNT_PPM is loading. */
export const REGULAR_BULK_DISCOUNT_RATE = 0.2;

/** DISCOUNT_PPM is divided by this to get percent (e.g. 200000 -> 20). */
export const PRESALE_DISCOUNT_PPM_DIVISOR = 10000;

export function presaleDiscountPercentFromPpm(raw) {
  if (raw == null) return 0;
  try {
    return new BigNumber(String(raw))
      .div(PRESALE_DISCOUNT_PPM_DIVISOR)
      .toNumber();
  } catch {
    return 0;
  }
}

/** Fraction off list price (e.g. 20% -> 0.2). */
export function presaleDiscountRateFromPpm(raw) {
  const pct = presaleDiscountPercentFromPpm(raw);
  if (!Number.isFinite(pct) || pct <= 0) return 0;
  return pct / 100;
}

export function regularBulkDiscountPercentFromRate(
  rate = REGULAR_BULK_DISCOUNT_RATE,
) {
  return Math.round((Number(rate) || 0) * 100);
}

/**
 * Reads presale round USD caps and allocation counters from the presale contract.
 * @param {{ account?: string, rpc?: string, contractAddress?: string }} [options]
 */
export async function fetchPresaleRoundLimitsFromChain(options = {}) {
  const {
    account,
    rpc = Environment.rpc,
    contractAddress = Environment.presaleContract,
  } = options;

  const web3 = new Web3(rpc);
  const contract = new web3.eth.Contract(ChinaAbi, contractAddress);

  const [
    gemsRoundCapWei,
    totalGemsRoundSoldWei,
    gemsLimitRaw,
    specialLimitRaw,
    lowerLimitRaw,
    upperLimitRaw,
    discountPpmRaw,
  ] = await Promise.all([
    contract.methods.gemsRoundCap().call(),
    contract.methods.totalGemsRoundSold().call(),
    contract.methods.gemsRoundLimitPerWallet().call(),
    contract.methods.specialRoundLimit().call(),
    contract.methods.lowerLimit().call(),
    contract.methods.upperLimit().call(),
    contract.methods.DISCOUNT_PPM().call(),
  ]);

  const discountRate = presaleDiscountRateFromPpm(discountPpmRaw);
  const discountPercent = presaleDiscountPercentFromPpm(discountPpmRaw);

  const result = {
    gemsRoundUsdCap: presaleUsdFrom6Decimals(gemsLimitRaw),
    specialRoundUsdCap: presaleUsdFrom6Decimals(specialLimitRaw),
    regularInvestedThresholdUsd: presaleUsdFrom6Decimals(lowerLimitRaw),
    regularMinOrderUsd: presaleUsdFrom6Decimals(upperLimitRaw),
    gemsRoundCapWei: String(gemsRoundCapWei),
    totalGemsRoundSoldWei: String(totalGemsRoundSoldWei),
    gemsSpentUsd: 0,
    specialSpentUsd: 0,
    regularInvestedUsd: 0,
    regularBulkDiscountRate:
      discountRate > 0 ? discountRate : REGULAR_BULK_DISCOUNT_RATE,
    regularBulkDiscountPercent:
      discountPercent > 0
        ? discountPercent
        : regularBulkDiscountPercentFromRate(REGULAR_BULK_DISCOUNT_RATE),
  };

  if (account) {
    const [gemsBuyingRaw, specialBuyingRaw, regularInvestedRaw] =
      await Promise.all([
        contract.methods.gemsRoundBuying(account).call(),
        contract.methods.specialRoundBuying(account).call(),
        contract.methods.lowerLimitInvestment(account).call(),
      ]);
    result.gemsSpentUsd = presaleUsdFrom6Decimals(gemsBuyingRaw);
    result.specialSpentUsd = presaleUsdFrom6Decimals(specialBuyingRaw);
    result.regularInvestedUsd = presaleUsdFrom6Decimals(regularInvestedRaw);
  }

  return result;
}

/** Regular round: $50k+ single transaction gets 20% off the unit price. */
export function qualifiesForRegularBulkDiscount(
  roundKind,
  transactionUsd,
  minOrderUsd = REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD,
) {
  return (
    roundKind === "regular" &&
    meetsRegularBulkMinimumUsd(transactionUsd, minOrderUsd)
  );
}

export function getRegularBulkDiscountedPrice(
  basePriceUsd,
  discountRate = REGULAR_BULK_DISCOUNT_RATE,
) {
  const p = Number(basePriceUsd) || 0;
  const rate = Number(discountRate) || 0;
  if (p <= 0) return 0;
  if (rate <= 0) return p;
  if (rate >= 1) return 0;
  return p * (1 - rate);
}

/**
 * Unit price (USD per sale token) for purchase / confirm APIs.
 * Staking perk price takes precedence over regular bulk discount.
 */
export function getPurchasePriceForApi({
  basePriceUsd,
  discountedStakingPriceUsd,
  roundKind,
  amountInUsdt,
  hasPremiumStakingPerks = false,
  regularMinOrderUsd = REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD,
  regularBulkDiscountRate = REGULAR_BULK_DISCOUNT_RATE,
}) {
  if (hasPremiumStakingPerks) {
    return Number(discountedStakingPriceUsd) || 0;
  }
  if (
    qualifiesForRegularBulkDiscount(
      roundKind,
      amountInUsdt,
      regularMinOrderUsd,
    )
  ) {
    return getRegularBulkDiscountedPrice(
      basePriceUsd,
      regularBulkDiscountRate,
    );
  }
  return Number(basePriceUsd) || 0;
}

/** On-chain / float USD values within this band of a threshold count as reached. */
export const USD_COMPARE_EPSILON = 0.1;

/** USDT/USDC treated as depegged when price is below $1 by more than this amount. */
export const STABLECOIN_PEG_EPSILON = 0.0001;

/** USDT/USDC on-chain payment uses 6 decimal places. */
export const STABLECOIN_PAY_DECIMALS = 6;

/**
 * USD value of a stablecoin payment (payCrypto * price), high precision.
 */
export function stablecoinPayToUsd(payCrypto, paymentTokenUsdPrice) {
  const pay = new BigNumber(payCrypto || 0);
  const px = new BigNumber(paymentTokenUsdPrice || 0);
  if (!pay.isFinite() || !px.isFinite() || px.lte(0)) return 0;
  return pay.times(px).toNumber();
}

/**
 * Minimum USDT/USDC pay amount so pay * price >= minUsd (never below after 6-decimal rounding).
 */
export function computeStablecoinPayForMinUsd(
  minUsd,
  paymentTokenUsdPrice,
  payDecimals = STABLECOIN_PAY_DECIMALS,
) {
  const minBn = new BigNumber(minUsd || 0);
  const px = new BigNumber(paymentTokenUsdPrice || 0);
  if (!minBn.isFinite() || minBn.lte(0) || !px.isFinite() || px.lte(0)) {
    return null;
  }

  const unit = new BigNumber(10).pow(-payDecimals);
  let pay = minBn.div(px).decimalPlaces(payDecimals, BigNumber.ROUND_UP);

  while (pay.times(px).lt(minBn)) {
    pay = pay.plus(unit);
  }

  const usdValue = pay.times(px).toNumber();
  return {
    payCrypto: pay.toNumber(),
    payCryptoStr: pay.toFixed(payDecimals),
    usdValue,
  };
}

/** Strict $50k+ bulk minimum (no downward epsilon). */
export function meetsRegularBulkMinimumUsd(
  transactionUsd,
  minOrderUsd = REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD,
) {
  return (Number(transactionUsd) || 0) >= (Number(minOrderUsd) || 0);
}

/** Single purchase still within the $5k first-tier cap (stablecoin peg tolerance only). */
export function withinRegularFirstPurchaseCapUsd(
  transactionUsd,
  thresholdUsd = REGULAR_INVESTED_THRESHOLD_USD,
) {
  return (
    (Number(transactionUsd) || 0) <=
    (Number(thresholdUsd) || 0) + STABLECOIN_PEG_EPSILON
  );
}

/** Above $5k and below $50k — not allowed until the user has hit the $5k tier. */
export function inRegularPurchaseDeadZoneUsd(
  transactionUsd,
  thresholdUsd = REGULAR_INVESTED_THRESHOLD_USD,
  minBulkUsd = REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD,
) {
  const txUsd = Number(transactionUsd) || 0;
  if (txUsd <= 0) return false;
  return (
    !withinRegularFirstPurchaseCapUsd(txUsd, thresholdUsd) &&
    !meetsRegularBulkMinimumUsd(txUsd, minBulkUsd)
  );
}

/** `investedUsd >= thresholdUsd`, treating differences of `eps` or less as equal. */
export function investedAtOrAboveThreshold(
  investedUsd,
  thresholdUsd,
  eps = USD_COMPARE_EPSILON,
) {
  return (Number(investedUsd) || 0) >= thresholdUsd - eps;
}

/**
 * USDT/USDC below $1: user may enter the nominal $50k token minimum (e.g. 50000)
 * which undervalues the purchase. Bump payment to reach $50k USD (bulk / dead-zone bypass).
 * @returns {null | { enteredPayCrypto: number, enteredUsd: number, adjustedPayCrypto: number, targetUsd: number }}
 */
export function getStablecoinMinOrderAdjustment({
  roundKind,
  paymentToken,
  paymentTokenUsdPrice,
  enteredPayCrypto,
  regularMinOrderUsd = REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD,
}) {
  const pay = Math.max(0, Number(enteredPayCrypto) || 0);
  if (pay <= 0 || roundKind !== "regular") return null;
  if (paymentToken !== "USDT" && paymentToken !== "USDC") return null;

  const px = Number(paymentTokenUsdPrice) || 0;
  const minUsd = Number(regularMinOrderUsd) || REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD;
  if (px <= 0 || px >= 1 - STABLECOIN_PEG_EPSILON) return null;

  if (Math.abs(pay - minUsd) > 0.01) return null;

  const enteredUsd = stablecoinPayToUsd(pay, px);
  if (meetsRegularBulkMinimumUsd(enteredUsd, minUsd)) return null;

  const computed = computeStablecoinPayForMinUsd(minUsd, px);
  if (!computed) return null;

  return {
    enteredPayCrypto: pay,
    enteredUsd,
    adjustedPayCrypto: computed.payCrypto,
    adjustedPayCryptoStr: computed.payCryptoStr,
    targetUsd: computed.usdValue,
  };
}

/**
 * USD uint256 from presale contract (6 decimals): gemsRoundBuying,
 * specialRoundBuying, lowerLimitInvestment, lowerLimit, upperLimit,
 * gemsRoundLimitPerWallet, specialRoundLimit.
 */
export function presaleUsdFrom6Decimals(raw) {
  if (raw == null) return 0;
  try {
    return new BigNumber(String(raw))
      .div(new BigNumber(10).pow(PRESALE_USD_DECIMALS))
      .toNumber();
  } catch {
    return 0;
  }
}

/** @deprecated Use presaleUsdFrom6Decimals */
export const presaleUsdFromUint256 = presaleUsdFrom6Decimals;

/**
 * Sale token uint256 (18 decimals): gemsRoundCap, totalGemsRoundSold, maxCap.
 */
export function presaleTokenFrom18Decimals(raw) {
  if (raw == null) return 0;
  try {
    return new BigNumber(String(raw))
      .div(new BigNumber(10).pow(PRESALE_TOKEN_DECIMALS))
      .toNumber();
  } catch {
    return 0;
  }
}

export function fmtUsd(n) {
  return formatDisplayUsd(n, 2);
}

export function fmtCredits(n) {
  return formatDisplayNumber(Math.max(0, Number(n) || 0), 0);
}

/**
 * Credits not yet unlocked by prior special-round spending (1 credit ≈ $1 permission).
 * @param {number} totalCreditUsd — wallet credits from API (remainingCredit)
 * @param {number} specialSpentUsd — on-chain specialRoundBuying for this wallet
 */
export function getSpecialRoundCreditsRemainingToUnlock(
  totalCreditUsd,
  specialSpentUsd,
) {
  const total = Math.max(0, Number(totalCreditUsd) || 0);
  const spent = Math.max(0, Number(specialSpentUsd) || 0);
  return Math.max(0, total - Math.min(total, spent));
}

/**
 * Credits unlocked by a single special-round purchase (success modal / summaries).
 * @param {number} totalCreditUsd — wallet credits from API
 * @param {number} specialSpentBeforeUsd — on-chain specialRoundBuying before this purchase
 * @param {number} purchaseUsd — this transaction USD
 */
export function getKaiCreditsUnlockedBySpecialPurchase(
  totalCreditUsd,
  specialSpentBeforeUsd,
  purchaseUsd,
) {
  const purchase = Math.max(0, Number(purchaseUsd) || 0);
  if (purchase <= 0) return 0;

  const unlockable = getSpecialRoundCreditsRemainingToUnlock(
    totalCreditUsd,
    specialSpentBeforeUsd,
  );
  return Math.min(unlockable, purchase);
}

/**
 * Special round: credit permission hints (non-blocking).
 * @param {number} transactionUsd — current purchase USD
 * @param {number} remainingCreditUsd — response.data.data.remainingCredit (>= 0)
 * @param {number} [specialSpentUsd] — on-chain specialRoundBuying (prior purchases in this round)
 * @returns {string|null}
 */
export function getSpecialRoundCreditHint(
  transactionUsd,
  remainingCreditUsd,
  specialSpentUsd = 0,
) {
  const txUsd = Math.max(0, Number(transactionUsd) || 0);
  const spent = Math.max(0, Number(specialSpentUsd) || 0);
  const totalCredit = Math.max(0, Number(remainingCreditUsd) || 0);
  const alreadyUnlocked = Math.min(spent, totalCredit);
  const credits = getSpecialRoundCreditsRemainingToUnlock(
    remainingCreditUsd,
    specialSpentUsd,
  );

  if (txUsd <= 0) return null;

  const totalUnlocked = alreadyUnlocked + txUsd;
  const roundUnlockSummary =
    alreadyUnlocked > 0
      ? ` You already unlocked $${fmtUsd(alreadyUnlocked)} in this round, so your total unlocked credit permission will be $${fmtUsd(totalUnlocked)}.`
      : "";

  if (credits <= 0) {
    if (spent > 0) {
      return (
        `Heads up — this purchase unlocks an additional $${fmtUsd(txUsd)} in credit permission.${roundUnlockSummary} ` +
        "However, you don't have any available credits remaining. " +
        "This additional permission won't be usable for purchasing $KAI."
      );
    }
    return (
      "You have $0 in available credits. This round only unlocks permission for existing credits — " +
      "your purchase will grant $KAI tokens normally, but no redeemable permission."
    );
  }

  if (txUsd > credits) {
    const extra = txUsd - credits;
    return (
      `Heads up — this purchase unlocks an additional $${fmtUsd(txUsd)} in credit permission.${roundUnlockSummary} ` +
      `However, you only have $${fmtUsd(credits)} in available credits remaining. ` +
      `The extra $${fmtUsd(extra)} won't be usable for purchasing $KAI.`
    );
  }

  return null;
}

/**
 * Remaining sale token (18 decimals) still available in the gems round allocation.
 * @param {string|number|null|undefined} capWei
 * @param {string|number|null|undefined} soldWei
 * @returns {number} human token amount, or NaN if inputs are invalid
 */
export function gemsRoundKaiRemainingFromWei(capWei, soldWei) {
  try {
    const cap = new BigNumber(String(capWei ?? "0"));
    const sold = new BigNumber(String(soldWei ?? "0"));
    if (!cap.isFinite() || !sold.isFinite()) return NaN;
    const rem = cap.gt(sold) ? cap.minus(sold) : new BigNumber(0);
    return rem
      .div(new BigNumber(10).pow(PRESALE_TOKEN_DECIMALS))
      .toNumber();
  } catch {
    return NaN;
  }
}

// /**
//  * @param {object} p
//  * @param {"regular"|"gems"|"special"} p.roundKind
//  * @param {string} p.mainTag
//  * @param {number|null|undefined} p.amountInUsdt
//  * @param {number} p.gemsSpentUsd
//  * @param {number} p.specialSpentUsd
//  * @param {number} p.regularInvestedUsd
//  */
// export function getRoundInvestmentValidation(p) {
//   const v = getPresaleRealtimeValidation({
//     ...p,
//     paymentTokenBalance: Number.POSITIVE_INFINITY,
//     payAmountRaw: "",
//     insuranceChecked: false,
//     insurancePer: 0,
//     gemsRoundCapWei: null,
//     totalGemsRoundSoldWei: null,
//     kaiReceiveAmount: 0,
//     saleTokenSymbol: "KAI",
//     kaiPriceUsd: 0,
//   });
//   return { ok: v.ok, message: v.error };
// }

/**
 * Full presale form validation for realtime UI (balance + round rules + hints).
 * @param {object} p
 * @param {"regular"|"gems"|"special"} p.roundKind
 * @param {string} p.mainTag
 * @param {number|null|undefined} p.amountInUsdt
 * @param {number} p.gemsSpentUsd
 * @param {number} p.specialSpentUsd
 * @param {number} p.regularInvestedUsd — lowerLimitInvestment (6 decimals on-chain)
 * @param {number} [p.gemsRoundUsdCap] — gemsRoundLimitPerWallet (6 decimals)
 * @param {number} [p.specialRoundUsdCap] — specialRoundLimit (6 decimals)
 * @param {number} [p.regularInvestedThresholdUsd] — lowerLimit (6 decimals)
 * @param {number} [p.regularMinOrderUsd] — upperLimit (6 decimals)
 * @param {number} [p.regularBulkDiscountPercent] — DISCOUNT_PPM / 10000
 * @param {number} [p.remainingCreditUsd] — special round remainingCredit from API
 * @param {number} p.paymentTokenBalance
 * @param {string|number} p.payAmountRaw — payment input (e.g. ETH or GEMS amount)
 * @param {boolean} p.insuranceChecked
 * @param {number} p.insurancePer — fraction e.g. 0.05 for 5%
 * @param {string|null|undefined} p.gemsRoundCapWei
 * @param {string|null|undefined} p.totalGemsRoundSoldWei
 * @param {number} p.kaiReceiveAmount — $KAI to receive (from inputFeild1)
 * @param {string} [p.saleTokenSymbol]
 * @param {number} [p.kaiPriceUsd] — USD per $KAI (Banner `price`)
 * @param {boolean} [p.isSpecialUser] — bypass gems round per-wallet USD cap
 * @returns {{ ok: boolean, error: string|null, hint: string|null }}
 */
export function getPresaleRealtimeValidation(p) {
  const {
    roundKind,
    mainTag,
    amountInUsdt,
    gemsSpentUsd,
    specialSpentUsd,
    regularInvestedUsd,
    isSpecialUser = false,
    gemsRoundUsdCap = GEMS_ROUND_USD_CAP,
    specialRoundUsdCap = SPECIAL_ROUND_USD_CAP,
    regularInvestedThresholdUsd = REGULAR_INVESTED_THRESHOLD_USD,
    regularMinOrderUsd = REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD,
    regularBulkDiscountPercent = regularBulkDiscountPercentFromRate(
      REGULAR_BULK_DISCOUNT_RATE,
    ),
    remainingCreditUsd = 0,
    paymentTokenBalance,
    payAmountRaw,
    insuranceChecked,
    insurancePer,
    gemsRoundCapWei,
    totalGemsRoundSoldWei,
    kaiReceiveAmount,
    saleTokenSymbol = "KAI",
    kaiPriceUsd = 0,
  } = p;

  const txUsd = Math.max(0, Number(amountInUsdt) || 0);
  const payStr =
    payAmountRaw === "" || payAmountRaw == null
      ? ""
      : String(payAmountRaw).trim();
  const pay = payStr === "" ? 0 : Math.max(0, parseFloat(payStr) || 0);
  const ins = Number(insurancePer) || 0;
  const totalPay = insuranceChecked ? pay + pay * ins : pay;
  const balance = Math.max(0, Number(paymentTokenBalance) || 0);

  if (pay > 0 && totalPay > balance + 1e-12) {
    return {
      ok: false,
      error: `Insufficient ${mainTag} balance.`,
      hint: null,
    };
  }

  if (roundKind === "gems" && mainTag !== "GEMS") {
    return {
      ok: false,
      error: "Gems round: pay with GEMS only.",
      hint: null,
    };
  }

  const spentGems = Number(gemsSpentUsd) || 0;
  const gemsCapUsd = Number(gemsRoundUsdCap) || GEMS_ROUND_USD_CAP;
  const specialCapUsd = Number(specialRoundUsdCap) || SPECIAL_ROUND_USD_CAP;

  if (
    !isSpecialUser &&
    roundKind === "gems" &&
    txUsd > 0 &&
    spentGems >= gemsCapUsd - USD_COMPARE_EPSILON
  ) {
    return {
      ok: false,
      error: `You've already reached your $${fmtUsd(gemsCapUsd)} user limit for this round.`,
      hint: null,
    };
  }

  if (
    !isSpecialUser &&
    roundKind === "gems" &&
    txUsd > 0 &&
    spentGems === 0 &&
    txUsd > gemsCapUsd
  ) {
    return {
      ok: false,
      error: `Amount exceeds the $${fmtUsd(gemsCapUsd)} per-user limit for this round. Please reduce your amount.`,
      hint: null,
    };
  }

  if (
    roundKind === "gems" &&
    txUsd > 0 &&
    gemsRoundCapWei != null &&
    totalGemsRoundSoldWei != null
  ) {
    const remainingKai = gemsRoundKaiRemainingFromWei(
      gemsRoundCapWei,
      totalGemsRoundSoldWei,
    );
    const want = Math.max(0, Number(kaiReceiveAmount) || 0);
    if (Number.isFinite(remainingKai) && want > remainingKai + 1e-12) {
      const px = Math.max(0, Number(kaiPriceUsd) || 0);
      const remUsd = remainingKai * px;
      const remKaiStr = remainingKai.toLocaleString(undefined, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      });
      const remUsdStr = remUsd.toLocaleString(undefined, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      });
      return {
        ok: false,
        error: `Not enough $${saleTokenSymbol} available in this round. Only ${remKaiStr} $${saleTokenSymbol} (~$${remUsdStr}) remaining. Please reduce your amount.`,
        hint: null,
      };
    }
  }

  const spentSpecial = Number(specialSpentUsd) || 0;
  if (
    roundKind === "special" &&
    txUsd > 0 &&
    spentSpecial === 0 &&
    txUsd > specialCapUsd
  ) {
    return {
      ok: false,
      error: `Amount exceeds the $${fmtUsd(specialCapUsd)} per user limit for this round. Please reduce your amount.`,
      hint: null,
    };
  }

  if (
    !isSpecialUser &&
    roundKind === "gems" &&
    txUsd > 0 &&
    spentGems > 0
  ) {
    if (spentGems < gemsCapUsd) {
      const room = Math.max(0, gemsCapUsd - spentGems);
      const gemsPartialMsg = `You've already invested $${fmtUsd(spentGems)} in this round. You can add up to $${fmtUsd(room)} more before reaching your $${fmtUsd(gemsCapUsd)} user limit.`;
      if (spentGems + txUsd > gemsCapUsd + USD_COMPARE_EPSILON) {
        return { ok: false, error: gemsPartialMsg, hint: null };
      }
    }
  }

  if (roundKind === "special" && txUsd > 0 && spentSpecial > 0) {
    if (spentSpecial < specialCapUsd) {
      const room = Math.max(0, specialCapUsd - spentSpecial);
      const specialPartialMsg = `You've already invested $${fmtUsd(spentSpecial)} in this round. You can add up to $${fmtUsd(room)} more before reaching your $${fmtUsd(specialCapUsd)} user limit.`;
      if (spentSpecial + txUsd > specialCapUsd + USD_COMPARE_EPSILON) {
        return { ok: false, error: specialPartialMsg, hint: null };
      }
    }
  }

  const regularInv = Number(regularInvestedUsd) || 0;
  const regularThreshold =
    Number(regularInvestedThresholdUsd) || REGULAR_INVESTED_THRESHOLD_USD;
  const regularMinBulk =
    Number(regularMinOrderUsd) || REGULAR_MIN_ORDER_AFTER_THRESHOLD_USD;
  const regularLimitReached = investedAtOrAboveThreshold(
    regularInv,
    regularThreshold,
  );

  if (roundKind === "regular" && txUsd > 0) {
    const inRegularDeadZone = inRegularPurchaseDeadZoneUsd(
      txUsd,
      regularThreshold,
      regularMinBulk,
    );

    if (!regularLimitReached && inRegularDeadZone) {
      return {
        ok: false,
        error: `Please enter an amount less than $${fmtUsd(regularThreshold)} or more than $${fmtUsd(regularMinBulk)}.`,
        hint: null,
      };
    }

    if (regularLimitReached) {
      // upperLimit+ single transaction is always allowed (bulk discount).
      if (meetsRegularBulkMinimumUsd(txUsd, regularMinBulk)) {
        // valid
      } else {
        return {
          ok: false,
          error: `Limit per user reached. You've already invested $${fmtUsd(regularInv)} in this sale. To purchase more, enter $${fmtUsd(regularMinBulk)}+ in a single transaction and get a ${regularBulkDiscountPercent}% discount.`,
          hint: null,
        };
      }
    } else {
      const room = Math.max(0, regularThreshold - regularInv);
      if (
        txUsd > room + STABLECOIN_PEG_EPSILON &&
        withinRegularFirstPurchaseCapUsd(txUsd, regularThreshold)
      ) {
        return {
          ok: false,
          error: `Amount exceeds your remaining $${fmtUsd(room)} limit for this sale. Please reduce your amount.`,
          hint: null,
        };
      }
    }
  }

  let error = null;
  if (txUsd > 0) {
    if (
      !isSpecialUser &&
      roundKind === "gems" &&
      spentGems > 0 &&
      spentGems < gemsCapUsd &&
      spentGems + txUsd <= gemsCapUsd + USD_COMPARE_EPSILON
    ) {
      const room = Math.max(0, gemsCapUsd - spentGems);
      error = `You've already invested $${fmtUsd(spentGems)} in this round. You can add up to $${fmtUsd(room)} more before reaching your $${fmtUsd(gemsCapUsd)} user limit.`;
    } else if (roundKind === "special" && spentSpecial > 0) {
      if (
        spentSpecial < specialCapUsd &&
        spentSpecial + txUsd <= specialCapUsd + USD_COMPARE_EPSILON
      ) {
        const room = Math.max(0, specialCapUsd - spentSpecial);
        error = `You've already invested $${fmtUsd(spentSpecial)} in this round. You can add up to $${fmtUsd(room)} more before reaching your $${fmtUsd(specialCapUsd)} user limit.`;
      }
    }
  }

  let hint = null;
  if (roundKind === "regular" && txUsd > 0 && !regularLimitReached) {
    const room = Math.max(0, regularThreshold - regularInv);
    if (regularInv > 0 && txUsd <= room + USD_COMPARE_EPSILON) {
      hint = `You've already invested $${fmtUsd(regularInv)} in this sale. You can add up to $${fmtUsd(room)} more before reaching your $${fmtUsd(regularThreshold)} user limit.`;
    }
  } else if (roundKind === "special" && txUsd > 0) {
    hint = getSpecialRoundCreditHint(
      txUsd,
      remainingCreditUsd,
      specialSpentUsd,
    );
  }

  return { ok: true, error, hint };
}
